import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../services/axios";
import { fetchAllPaginated } from "../../services/fetchPaginated";
import { Device, isDeviceDetailLoaded } from "../../lib/Device";

interface DevicesData extends Array<Device> {}

const mergeDevice = (devices: Device[], device: Device): Device[] => {
  const updated = [...devices];
  const index = updated.findIndex((entry) => entry.id === device.id);
  if (index >= 0) {
    updated[index] = { ...updated[index], ...device };
  } else {
    updated.push(device);
  }
  return updated;
};

export const fetchDevices = createAsyncThunk(
  "devices/fetchDevices",
  async () => {
    return await fetchAllPaginated<Device>("/api-v1/device/?page_size=100").catch(
      () => []
    );
  }
);

export const fetchDevice = createAsyncThunk(
  "devices/fetchDevice",
  async (id: number) => {
    const response = await axios.get(`/api-v1/device/${id}/`);
    return response.data as Device;
  }
);

export const createDevice = createAsyncThunk(
  "devices/createDevice",
  async (data: any) => {
    return data;
  }
);

export const updateDevice = createAsyncThunk(
  "devices/updateDevice",
  async ({
    id,
    data,
    updated = false,
  }: {
    id: number;
    data: any;
    updated: boolean;
  }) => {
    if (updated) {
      return data;
    }
    const response = await axios.patch(`/api-v1/device/${id}/`, data);
    return response.data;
  }
);

export const deleteDevice = createAsyncThunk(
  "devices/deleteDevice",
  async (id: number) => {
    await axios.delete(`/api-v1/device/${id}/`);
    return id;
  }
);

const devicesSlice = createSlice({
  name: "devices",
  initialState: null as DevicesData | null,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchDevices.fulfilled, (state, { payload }) => {
      payload.sort((a: Device, b: Device) => a.id - b.id);
      if (!state) {
        return payload;
      }
      const byId = new Map(state.map((device: Device) => [device.id, device]));
      return payload.map((summary: Device) => {
        const existing = byId.get(summary.id);
        if (existing && isDeviceDetailLoaded(existing)) {
          return { ...summary, ...existing };
        }
        return summary;
      });
    });
    builder.addCase(fetchDevice.fulfilled, (state: any, { payload }) => {
      if (!state) {
        return [payload];
      }
      const merged = mergeDevice(state, payload);
      merged.sort((a: Device, b: Device) => a.id - b.id);
      return merged;
    });
    builder.addCase(createDevice.fulfilled, (state: any, { payload }) => {
      return [...state, payload];
    });
    builder.addCase(updateDevice.fulfilled, (state: any, { payload }) => {
      const updated = [...state];
      const old = updated.find((e) => e.id === payload.id);
      if (old) {
        updated.splice(updated.indexOf(old), 1, payload);
      } else {
        return [...state, payload];
      }
      return updated;
    });
    builder.addCase(deleteDevice.fulfilled, (state: any, { payload }) => {
      const updated = [...state];
      const old = updated.find((e) => e.id === payload);
      if (old) {
        updated.splice(updated.indexOf(old), 1);
      }
      return updated;
    });
  },
});

export const devicesMethods = {
  fetchDevices,
  fetchDevice,
  createDevice,
  updateDevice,
  deleteDevice,
};

export default devicesSlice.reducer;
